/* istanbul ignore file */
/******************************************
 *  Author : vsuthar
 *  Created On : Thu Sep 02 2021
 *  File : index.js
 *  ©Palo Alto Networks
 *******************************************/

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { noop } from '@panwds/react-ui'
import Identities from './components/identities/Identities'
import Roles from './components/roles/Roles'
import RCTree from '../common/components/page/rcTree/RCTree'
import {
  VIEW_MANAGE_IAM,
  VIEW_IAM_ROLES,
  VIEW_IAM_PROVIDERS,
  VIEW_ADD_CUSTOM_ROLE,
  VIEW_EDIT_CUSTOM_ROLE,
} from '../common/constants/GlobalConstants'
import BreadCrumbs from '../common/components/breadcrumbs/BreadCrumbs'
import CustomRoleForm from './components/roles/CustomRoleForm'
import { useMessage } from '../common/providers/MessageProvider'
import IDP from './components/idp/IDP'
import GlobalContext from '../common/context/GlobalContext'
import LoadingProvider, { useLoadingContext } from '../common/context/LoadingContext'
import {
  resetIAMReducer as resetAction,
  setAccessPolicies,
  setCustomRoles,
  setServiceAccounts,
} from '../common/reducers/actions/IAMReducer.actions'
import IAMService from './services/IAMService'
import { getVarValue } from '../common/utils/Sparky'
import {
  FF_CUSTOM_ROLES_ENABLED
} from '../common/utils/FeatureFlags'
import IpRestrictProvider, { useIpRestrictionContext } from '../common/context/IpRestrictContext'
import IPRestrictionWrapper from '../common/components/noAccess/IPRestrictionWrapper'

const LoadCustomRoles = tsg_id => IAMService.customRoles.list(tsg_id).then(({ data }) => data.items)
const LoadServiceAccounts = tsg_id => IAMService.serviceAccounts.list(tsg_id).then(({ data }) => data.items)
const LoadAccessPolicies = tsg_id => IAMService.accessPolicies.list(tsg_id).then(({ data }) => data.items)

const customEnabled = getVarValue(FF_CUSTOM_ROLES_ENABLED, false)

const IAMContent = (props) => {

  const { dispatch } = useContext(GlobalContext)
  const { selectedTreeNode, breadcrumbs, onBreadCrumbClick, currentView } = props
  const { clearBanner, createBanner, apiBannerHandler } = useMessage()
  const { setLoading } = useLoadingContext()
  const { checkIpRestricted, setRestricted } = useIpRestrictionContext()
  const tsgRef = useRef()

  const [canListPolicies, setCanListPolicies] = useState(true)
  const [canListRoles, setCanListRoles] = useState(true)

  const resetIAMReducer = useCallback(() => dispatch(resetAction()), [dispatch])

  const loadAccessPolicies = useCallback(async (tsg_id) => {
    setCanListPolicies(true)
    return await LoadAccessPolicies(tsg_id)
      .catch(error => {
        if (tsgRef.current?.id === tsg_id && error?.response?.status === 403) {
          if (!checkIpRestricted(error)) {
            apiBannerHandler(error)
          }
          setCanListPolicies(false)
        }
        return []
      })
  }, [apiBannerHandler, checkIpRestricted])

  const loadServiceAccounts = useCallback(async (tsg_id) => {
    setCanListPolicies(true)
    return await LoadServiceAccounts(tsg_id)
      .catch(error => {
        if (tsgRef.current?.id === tsg_id && error?.response?.status === 403) {
          if (!checkIpRestricted(error)) {
            apiBannerHandler(error)
          }
        }
        return []
      })
  }, [apiBannerHandler, checkIpRestricted])

  const loadCustomRoles = useCallback(async (tsg_id) => {
    if (customEnabled) {
      setCanListRoles(true)
      return await LoadCustomRoles(tsg_id)
        .catch(error => {
          if (tsgRef.current?.id === tsg_id && error?.response?.status === 403) {
            if (!checkIpRestricted(error)) {
              apiBannerHandler(error)
            }
            setCanListRoles(false)
          }
          return []
        })
    }
    return []
  }, [apiBannerHandler, checkIpRestricted])

  const getData = useCallback(async (tsg_id, dispatch) => {
    resetIAMReducer()
    setRestricted(false)
    if (tsg_id && tsg_id !== '_superRoot') {
      try {
        setLoading(true)

        const accessPolicies = await loadAccessPolicies(tsg_id)
        const serviceAccounts = await loadServiceAccounts(tsg_id)
        const customRoles = await loadCustomRoles(tsg_id)

        if (tsgRef.current?.id === tsg_id) {
          dispatch(setServiceAccounts(serviceAccounts))
          dispatch(setAccessPolicies(accessPolicies))
          if (customEnabled) {
            dispatch(setCustomRoles(customRoles))
          }
          setLoading(false)
        }
        return [serviceAccounts, accessPolicies, customRoles]
      }
      catch (error) {
        if (tsgRef.current?.id === tsg_id) {
          createBanner({ message: error?.message, bannerProps: { showIcon: true, appearance: 'error' } })
        }
        if (tsgRef.current?.id === tsg_id) {
          setLoading(false)
        }
        return []
      }
    }
  }, [resetIAMReducer, setRestricted, setLoading, loadAccessPolicies, loadServiceAccounts, loadCustomRoles, createBanner])

  useEffect(() => {
    tsgRef.current = selectedTreeNode
    const tsg_id = selectedTreeNode?.id
    if (tsg_id) {
      getData(tsg_id, dispatch)
    }
  }, [dispatch, getData, selectedTreeNode])

  useEffect(() => {
    return clearBanner
  }, [clearBanner])

  const page = useMemo(() => {
    switch (currentView) {
      case VIEW_EDIT_CUSTOM_ROLE:
      case VIEW_ADD_CUSTOM_ROLE:
        return <CustomRoleForm {...{ getData }}/>
      case VIEW_IAM_ROLES:
        return <Roles {...{ getData, canListRoles }}/>
      case VIEW_IAM_PROVIDERS:
        return <IDP/>
      case VIEW_MANAGE_IAM:
      default:
        return <Identities {...{ getData, canListPolicies }} />
    }
  }, [currentView, getData, canListRoles, canListPolicies])

  const showSideBar = useMemo(() => {
    switch (currentView) {
      case VIEW_EDIT_CUSTOM_ROLE:
      case VIEW_IAM_PROVIDERS:
      case VIEW_ADD_CUSTOM_ROLE:
        return false
      case VIEW_IAM_ROLES:
      case VIEW_MANAGE_IAM:
      default:
        return true
    }
  }, [currentView])

  return (
    <>
      <div className="cm-flex cm-flex-1 cm-flex-row cm-absolute cm-inset-0">
        {showSideBar && (
          <div className="common-module-side-bar dark:cm-bg-gray-950">
            <RCTree
              {...props}
              expandedKeys={[selectedTreeNode?.parent_id]}
            />
          </div>
        )}
        <IPRestrictionWrapper>
          <div className='cm-flex cm-flex-col cm-border-t cm-flex-1 cm-overflow-auto'>
            {showSideBar && (
              <div className='cm-p-3 cm-flex-0' style={{ minHeight: '63px' }}>
                <BreadCrumbs breadcrumbs={breadcrumbs} onClick={onBreadCrumbClick} />
              </div>
            )}
            {page}
          </div>
        </IPRestrictionWrapper>
      </div>
    </>
  )
}

const IAM = (props) => (
  <IpRestrictProvider>
    <LoadingProvider>
      <IAMContent {...props} />
    </LoadingProvider>
  </IpRestrictProvider>
)

IAM.defaultProps = {
  selectedTreeNode: {},
  breadcrumbs: new Map(),
  onBreadCrumbClick: noop,
  currentView: null,
}
export default IAM
